/** @module @liquidefy/code-editor/workspace/workspace-layout @version 1.0.0 @license LQD Research LLC */

import type {
  EditorResult,
  LayoutType,
  LayoutNode,
  LayoutNodeId,
  LayoutLeafData,
  WorkspaceLayout,
  PanelId,
  PanelType,
  PanelPosition,
  PanelState,
  TabId,
  TabGroupId,
  TabState,
  TabGroupState,
  EditorInstanceId,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asLayoutNodeId,
  asPanelId,
  asTabId,
  asTabGroupId,
  now,
} from '../code-editor.js';

// ═══ §1 IMPORTS — re-exported above ═══

// ═══ §2 LAYOUT CREATION — factory functions for workspace layouts ═══

export function createLayout(
  type: LayoutType,
  orientation: 'horizontal' | 'vertical' = 'horizontal'
): EditorResult<WorkspaceLayout> {
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: [],
    direction: orientation,
  };
  const layout: WorkspaceLayout = { type, rootNode, orientation };
  return ok(layout, 'LAYOUT_CREATED', `Created ${type} layout`);
}

export function createSplitLayout(
  direction: 'horizontal' | 'vertical',
  sizes?: number[]
): EditorResult<WorkspaceLayout> {
  const children: LayoutNode[] = (sizes ?? [0.5, 0.5]).map((s) => ({
    id: asLayoutNodeId(generateId('ln')),
    type: 'leaf' as const,
    size: s,
    data: {} as LayoutLeafData,
  }));
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children,
    direction,
  };
  const layout: WorkspaceLayout = { type: 'split', rootNode, orientation: direction };
  return ok(layout, 'SPLIT_LAYOUT_CREATED', `Created split layout (${direction})`);
}

export function createTabbedLayout(): EditorResult<WorkspaceLayout> {
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'tab-group',
    children: [],
    activeChild: 0,
  };
  const layout: WorkspaceLayout = { type: 'tabbed', rootNode, orientation: 'horizontal' };
  return ok(layout, 'TABBED_LAYOUT_CREATED', 'Created tabbed layout');
}

export function createGridLayout(rows: number, cols: number): EditorResult<WorkspaceLayout> {
  if (rows < 1 || cols < 1) return err('INVALID_GRID', 'Grid must have at least 1 row and 1 column');
  const rowSize = 1 / rows;
  const colSize = 1 / cols;
  const rowChildren: LayoutNode[] = [];
  for (let r = 0; r < rows; r++) {
    const colChildren: LayoutNode[] = [];
    for (let c = 0; c < cols; c++) {
      colChildren.push({
        id: asLayoutNodeId(generateId('ln')),
        type: 'leaf',
        size: colSize,
        data: {} as LayoutLeafData,
      });
    }
    rowChildren.push({
      id: asLayoutNodeId(generateId('ln')),
      type: 'split',
      children: colChildren,
      direction: 'horizontal',
      size: rowSize,
    });
  }
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: rowChildren,
    direction: 'vertical',
  };
  const layout: WorkspaceLayout = { type: 'grid', rootNode, orientation: 'vertical' };
  return ok(layout, 'GRID_LAYOUT_CREATED', `Created ${rows}x${cols} grid layout`);
}

export function createFloatingLayout(): EditorResult<WorkspaceLayout> {
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: [],
    direction: 'horizontal',
  };
  const layout: WorkspaceLayout = { type: 'floating', rootNode, orientation: 'horizontal' };
  return ok(layout, 'FLOATING_LAYOUT_CREATED', 'Created floating layout');
}

export function createMosaicLayout(): EditorResult<WorkspaceLayout> {
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: [],
    direction: 'horizontal',
  };
  const layout: WorkspaceLayout = { type: 'mosaic', rootNode, orientation: 'horizontal' };
  return ok(layout, 'MOSAIC_LAYOUT_CREATED', 'Created mosaic layout');
}

export function createZenLayout(): EditorResult<WorkspaceLayout> {
  const leafNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'leaf',
    size: 1,
    data: {} as LayoutLeafData,
  };
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: [leafNode],
    direction: 'horizontal',
  };
  const layout: WorkspaceLayout = { type: 'zen', rootNode, orientation: 'horizontal' };
  return ok(layout, 'ZEN_LAYOUT_CREATED', 'Created zen layout');
}

export function createFocusLayout(): EditorResult<WorkspaceLayout> {
  const leafNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'leaf',
    size: 1,
    data: {} as LayoutLeafData,
  };
  const rootNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: [leafNode],
    direction: 'horizontal',
  };
  const layout: WorkspaceLayout = { type: 'focus', rootNode, orientation: 'horizontal' };
  return ok(layout, 'FOCUS_LAYOUT_CREATED', 'Created focus layout');
}

// ═══ §3 LAYOUT NODES — tree manipulation for layout node hierarchy ═══

export function createSplitNode(
  direction: 'horizontal' | 'vertical',
  children: LayoutNode[],
  sizes?: number[]
): EditorResult<LayoutNode> {
  const sizedChildren = children.map((child, i) => {
    const size = sizes && i < sizes.length ? sizes[i] : 1 / children.length;
    return { ...child, size };
  });
  const node: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: sizedChildren,
    direction,
  };
  return ok(node, 'SPLIT_NODE_CREATED', `Created split node (${direction})`);
}

export function createTabGroupNode(
  children: LayoutNode[],
  activeIndex: number = 0
): EditorResult<LayoutNode> {
  const clamped = children.length > 0 ? Math.min(activeIndex, children.length - 1) : 0;
  const node: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'tab-group',
    children,
    activeChild: clamped,
  };
  return ok(node, 'TAB_GROUP_NODE_CREATED', 'Created tab group node');
}

export function createLeafNode(data: LayoutLeafData): EditorResult<LayoutNode> {
  const node: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'leaf',
    data,
  };
  return ok(node, 'LEAF_NODE_CREATED', 'Created leaf node');
}

export function addChild(
  parent: LayoutNode,
  child: LayoutNode,
  index?: number
): EditorResult<LayoutNode> {
  const children = parent.children ?? [];
  const idx = index !== undefined ? Math.min(index, children.length) : children.length;
  const updated = [...children.slice(0, idx), child, ...children.slice(idx)];
  return ok({ ...parent, children: updated }, 'CHILD_ADDED', 'Added child to node');
}

export function removeChild(parent: LayoutNode, childId: LayoutNodeId): EditorResult<LayoutNode> {
  const children = parent.children ?? [];
  const filtered = children.filter((c) => c.id !== childId);
  if (filtered.length === children.length) return err('CHILD_NOT_FOUND', `Child ${childId} not found`);
  return ok({ ...parent, children: filtered }, 'CHILD_REMOVED', 'Removed child from node');
}

export function replaceChild(
  parent: LayoutNode,
  oldId: LayoutNodeId,
  newChild: LayoutNode
): EditorResult<LayoutNode> {
  const children = parent.children ?? [];
  const idx = children.findIndex((c) => c.id === oldId);
  if (idx === -1) return err('CHILD_NOT_FOUND', `Child ${oldId} not found`);
  const updated = [...children.slice(0, idx), newChild, ...children.slice(idx + 1)];
  return ok({ ...parent, children: updated }, 'CHILD_REPLACED', 'Replaced child node');
}

export function findNode(root: LayoutNode, nodeId: LayoutNodeId): LayoutNode | undefined {
  if (root.id === nodeId) return root;
  if (!root.children) return undefined;
  for (const child of root.children) {
    const found = findNode(child, nodeId);
    if (found) return found;
  }
  return undefined;
}

export function parentOf(root: LayoutNode, nodeId: LayoutNodeId): LayoutNode | undefined {
  if (!root.children) return undefined;
  for (const child of root.children) {
    if (child.id === nodeId) return root;
    const found = parentOf(child, nodeId);
    if (found) return found;
  }
  return undefined;
}

export function depth(root: LayoutNode, nodeId: LayoutNodeId, currentDepth: number = 0): number {
  if (root.id === nodeId) return currentDepth;
  if (!root.children) return -1;
  for (const child of root.children) {
    const d = depth(child, nodeId, currentDepth + 1);
    if (d >= 0) return d;
  }
  return -1;
}

export function nodeCount(root: LayoutNode): number {
  let count = 1;
  if (root.children) {
    for (const child of root.children) {
      count += nodeCount(child);
    }
  }
  return count;
}

export function leafCount(root: LayoutNode): number {
  if (root.type === 'leaf') return 1;
  if (!root.children) return 0;
  let count = 0;
  for (const child of root.children) {
    count += leafCount(child);
  }
  return count;
}

// ═══ §4 SPLIT OPERATIONS — splitting, resizing, moving layout nodes ═══

function replaceNodeInTree(root: LayoutNode, nodeId: LayoutNodeId, replacement: LayoutNode): LayoutNode {
  if (root.id === nodeId) return replacement;
  if (!root.children) return root;
  const updatedChildren = root.children.map((child) => replaceNodeInTree(child, nodeId, replacement));
  const changed = updatedChildren.some((c, i) => c !== root.children![i]);
  return changed ? { ...root, children: updatedChildren } : root;
}

function removeNodeFromTree(root: LayoutNode, nodeId: LayoutNodeId): LayoutNode | undefined {
  if (root.id === nodeId) return undefined;
  if (!root.children) return root;
  const filtered = root.children
    .map((child) => removeNodeFromTree(child, nodeId))
    .filter((c): c is LayoutNode => c !== undefined);
  if (filtered.length === root.children.length && filtered.every((c, i) => c === root.children![i])) {
    return root;
  }
  if (filtered.length === 1 && root.type === 'split') return filtered[0];
  return { ...root, children: filtered };
}

export function splitHorizontal(
  layout: WorkspaceLayout,
  nodeId: LayoutNodeId,
  newLeaf: LayoutLeafData
): EditorResult<WorkspaceLayout> {
  const target = findNode(layout.rootNode, nodeId);
  if (!target) return err('NODE_NOT_FOUND', `Node ${nodeId} not found`);
  const leafNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'leaf',
    size: 0.5,
    data: newLeaf,
  };
  const splitNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: [{ ...target, size: 0.5 }, leafNode],
    direction: 'horizontal',
  };
  const newRoot = replaceNodeInTree(layout.rootNode, nodeId, splitNode);
  return ok({ ...layout, rootNode: newRoot }, 'SPLIT_HORIZONTAL', 'Split node horizontally');
}

export function splitVertical(
  layout: WorkspaceLayout,
  nodeId: LayoutNodeId,
  newLeaf: LayoutLeafData
): EditorResult<WorkspaceLayout> {
  const target = findNode(layout.rootNode, nodeId);
  if (!target) return err('NODE_NOT_FOUND', `Node ${nodeId} not found`);
  const leafNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'leaf',
    size: 0.5,
    data: newLeaf,
  };
  const splitNode: LayoutNode = {
    id: asLayoutNodeId(generateId('ln')),
    type: 'split',
    children: [{ ...target, size: 0.5 }, leafNode],
    direction: 'vertical',
  };
  const newRoot = replaceNodeInTree(layout.rootNode, nodeId, splitNode);
  return ok({ ...layout, rootNode: newRoot }, 'SPLIT_VERTICAL', 'Split node vertically');
}

export function removeSplit(
  layout: WorkspaceLayout,
  nodeId: LayoutNodeId
): EditorResult<WorkspaceLayout> {
  if (layout.rootNode.id === nodeId) return err('CANNOT_REMOVE_ROOT', 'Cannot remove root node');
  const newRoot = removeNodeFromTree(layout.rootNode, nodeId);
  if (!newRoot) return err('REMOVE_FAILED', 'Removing node would leave empty layout');
  return ok({ ...layout, rootNode: newRoot }, 'SPLIT_REMOVED', 'Removed split node and collapsed parent');
}

export function resizeSplit(
  layout: WorkspaceLayout,
  nodeId: LayoutNodeId,
  newSize: number
): EditorResult<WorkspaceLayout> {
  const node = findNode(layout.rootNode, nodeId);
  if (!node) return err('NODE_NOT_FOUND', `Node ${nodeId} not found`);
  const clamped = Math.max(0.05, Math.min(0.95, newSize));
  const updated = { ...node, size: clamped };
  const newRoot = replaceNodeInTree(layout.rootNode, nodeId, updated);
  return ok({ ...layout, rootNode: newRoot }, 'SPLIT_RESIZED', `Resized node to ${clamped}`);
}

export function evenSplit(
  layout: WorkspaceLayout,
  nodeId: LayoutNodeId
): EditorResult<WorkspaceLayout> {
  const node = findNode(layout.rootNode, nodeId);
  if (!node) return err('NODE_NOT_FOUND', `Node ${nodeId} not found`);
  if (!node.children || node.children.length === 0) return err('NO_CHILDREN', 'Node has no children to equalize');
  const evenSize = 1 / node.children.length;
  const updatedChildren = node.children.map((c) => ({ ...c, size: evenSize }));
  const updated = { ...node, children: updatedChildren };
  const newRoot = replaceNodeInTree(layout.rootNode, nodeId, updated);
  return ok({ ...layout, rootNode: newRoot }, 'EVEN_SPLIT', 'Equalized children sizes');
}

export function swapNodes(
  layout: WorkspaceLayout,
  nodeA: LayoutNodeId,
  nodeB: LayoutNodeId
): EditorResult<WorkspaceLayout> {
  const a = findNode(layout.rootNode, nodeA);
  const b = findNode(layout.rootNode, nodeB);
  if (!a) return err('NODE_NOT_FOUND', `Node ${nodeA} not found`);
  if (!b) return err('NODE_NOT_FOUND', `Node ${nodeB} not found`);
  const placeholderId = asLayoutNodeId(generateId('placeholder'));
  const placeholder: LayoutNode = { ...a, id: placeholderId };
  let root = replaceNodeInTree(layout.rootNode, nodeA, placeholder);
  root = replaceNodeInTree(root, nodeB, { ...a, size: b.size });
  root = replaceNodeInTree(root, placeholderId, { ...b, size: a.size });
  return ok({ ...layout, rootNode: root }, 'NODES_SWAPPED', 'Swapped two nodes');
}

export function moveNode(
  layout: WorkspaceLayout,
  nodeId: LayoutNodeId,
  targetId: LayoutNodeId,
  position: 'before' | 'after' | 'inside'
): EditorResult<WorkspaceLayout> {
  const node = findNode(layout.rootNode, nodeId);
  const target = findNode(layout.rootNode, targetId);
  if (!node) return err('NODE_NOT_FOUND', `Source node ${nodeId} not found`);
  if (!target) return err('NODE_NOT_FOUND', `Target node ${targetId} not found`);
  let root = removeNodeFromTree(layout.rootNode, nodeId);
  if (!root) return err('MOVE_FAILED', 'Moving node would leave empty layout');
  if (position === 'inside') {
    const targetInNew = findNode(root, targetId);
    if (!targetInNew) return err('TARGET_LOST', 'Target lost after source removal');
    const children = targetInNew.children ?? [];
    const updated = { ...targetInNew, children: [...children, node] };
    root = replaceNodeInTree(root, targetId, updated);
  } else {
    const parent = parentOf(root, targetId);
    if (!parent || !parent.children) return err('NO_PARENT', 'Target has no parent');
    const idx = parent.children.findIndex((c) => c.id === targetId);
    if (idx === -1) return err('TARGET_NOT_FOUND', 'Target not found in parent');
    const insertIdx = position === 'before' ? idx : idx + 1;
    const updatedChildren = [
      ...parent.children.slice(0, insertIdx),
      node,
      ...parent.children.slice(insertIdx),
    ];
    const updatedParent = { ...parent, children: updatedChildren };
    root = replaceNodeInTree(root, parent.id, updatedParent);
  }
  return ok({ ...layout, rootNode: root }, 'NODE_MOVED', `Moved node ${position} target`);
}

// ═══ §5 PANEL MANAGEMENT — sidebar, bottom, activity bar panels ═══

export function createPanel(
  id: string,
  type: PanelType,
  position: PanelPosition,
  size: number = 250
): EditorResult<PanelState> {
  const panel: PanelState = {
    id: asPanelId(id),
    type,
    position,
    isVisible: true,
    size,
    isCollapsed: false,
  };
  return ok(panel, 'PANEL_CREATED', `Created ${type} panel at ${position}`);
}

export function addPanel(panels: PanelState[], panel: PanelState): EditorResult<PanelState[]> {
  const exists = panels.some((p) => p.id === panel.id);
  if (exists) return err('PANEL_EXISTS', `Panel ${panel.id} already exists`);
  return ok([...panels, panel], 'PANEL_ADDED', `Added panel ${panel.id}`);
}

export function removePanel(panels: PanelState[], id: PanelId): EditorResult<PanelState[]> {
  const filtered = panels.filter((p) => p.id !== id);
  if (filtered.length === panels.length) return err('PANEL_NOT_FOUND', `Panel ${id} not found`);
  return ok(filtered, 'PANEL_REMOVED', `Removed panel ${id}`);
}

export function togglePanel(panels: PanelState[], id: PanelId): EditorResult<PanelState[]> {
  const idx = panels.findIndex((p) => p.id === id);
  if (idx === -1) return err('PANEL_NOT_FOUND', `Panel ${id} not found`);
  const panel = panels[idx]!;
  const updated = { ...panel, isVisible: !panel.isVisible };
  const result = [...panels.slice(0, idx), updated, ...panels.slice(idx + 1)];
  return ok(result, 'PANEL_TOGGLED', `Toggled panel ${id} visibility`);
}

export function collapsePanel(panels: PanelState[], id: PanelId): EditorResult<PanelState[]> {
  const idx = panels.findIndex((p) => p.id === id);
  if (idx === -1) return err('PANEL_NOT_FOUND', `Panel ${id} not found`);
  const updated = { ...panels[idx]!, isCollapsed: true };
  return ok([...panels.slice(0, idx), updated, ...panels.slice(idx + 1)], 'PANEL_COLLAPSED', `Collapsed panel ${id}`);
}

export function expandPanel(panels: PanelState[], id: PanelId): EditorResult<PanelState[]> {
  const idx = panels.findIndex((p) => p.id === id);
  if (idx === -1) return err('PANEL_NOT_FOUND', `Panel ${id} not found`);
  const updated = { ...panels[idx]!, isCollapsed: false };
  return ok([...panels.slice(0, idx), updated, ...panels.slice(idx + 1)], 'PANEL_EXPANDED', `Expanded panel ${id}`);
}

export function resizePanel(panels: PanelState[], id: PanelId, size: number): EditorResult<PanelState[]> {
  const idx = panels.findIndex((p) => p.id === id);
  if (idx === -1) return err('PANEL_NOT_FOUND', `Panel ${id} not found`);
  if (size < 50) return err('PANEL_TOO_SMALL', 'Panel minimum size is 50px');
  const updated = { ...panels[idx]!, size };
  return ok([...panels.slice(0, idx), updated, ...panels.slice(idx + 1)], 'PANEL_RESIZED', `Resized panel ${id} to ${size}`);
}

export function movePanel(
  panels: PanelState[],
  id: PanelId,
  position: PanelPosition
): EditorResult<PanelState[]> {
  const idx = panels.findIndex((p) => p.id === id);
  if (idx === -1) return err('PANEL_NOT_FOUND', `Panel ${id} not found`);
  const updated = { ...panels[idx]!, position };
  return ok([...panels.slice(0, idx), updated, ...panels.slice(idx + 1)], 'PANEL_MOVED', `Moved panel ${id} to ${position}`);
}

export function getPanelsByPosition(panels: PanelState[], position: PanelPosition): PanelState[] {
  return panels.filter((p) => p.position === position);
}

export function activePanels(panels: PanelState[]): PanelState[] {
  return panels.filter((p) => p.isVisible && !p.isCollapsed);
}

export function panelTotalSize(panels: PanelState[], position: PanelPosition): number {
  return panels
    .filter((p) => p.position === position && p.isVisible && !p.isCollapsed)
    .reduce((sum, p) => sum + p.size, 0);
}

// ═══ §6 TAB MANAGEMENT — tab groups, pin, preview, reorder ═══

export function createTab(
  id: string,
  groupId: string,
  label: string,
  options?: { uri?: string; editorId?: EditorInstanceId; isPinned?: boolean; isPreview?: boolean; isDirty?: boolean; iconPath?: string; description?: string }
): EditorResult<TabState> {
  const tab: TabState = {
    id: asTabId(id),
    groupId: asTabGroupId(groupId),
    label,
    uri: options?.uri,
    editorId: options?.editorId,
    isPinned: options?.isPinned ?? false,
    isPreview: options?.isPreview ?? false,
    isDirty: options?.isDirty ?? false,
    iconPath: options?.iconPath,
    description: options?.description,
  };
  return ok(tab, 'TAB_CREATED', `Created tab "${label}"`);
}

export function createTabGroup(
  id: string,
  tabs?: TabState[],
  activeTabId?: TabId
): EditorResult<TabGroupState> {
  const gid = asTabGroupId(id);
  const tabList = tabs ?? [];
  const group: TabGroupState = {
    id: gid,
    tabs: tabList,
    activeTabId: activeTabId ?? (tabList.length > 0 ? tabList[0]!.id : undefined),
    isActive: true,
  };
  return ok(group, 'TAB_GROUP_CREATED', `Created tab group ${id}`);
}

export function addTab(
  groups: TabGroupState[],
  groupId: TabGroupId,
  tab: TabState
): EditorResult<TabGroupState[]> {
  const idx = groups.findIndex((g) => g.id === groupId);
  if (idx === -1) return err('GROUP_NOT_FOUND', `Tab group ${groupId} not found`);
  const group = groups[idx]!;
  const exists = group.tabs.some((t) => t.id === tab.id);
  if (exists) return err('TAB_EXISTS', `Tab ${tab.id} already exists in group`);
  const updatedGroup: TabGroupState = {
    ...group,
    tabs: [...group.tabs, { ...tab, groupId }],
    activeTabId: group.activeTabId ?? tab.id,
  };
  return ok([...groups.slice(0, idx), updatedGroup, ...groups.slice(idx + 1)], 'TAB_ADDED', `Added tab ${tab.id}`);
}

export function removeTab(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi]!;
    const ti = group.tabs.findIndex((t) => t.id === tabId);
    if (ti === -1) continue;
    const newTabs = [...group.tabs.slice(0, ti), ...group.tabs.slice(ti + 1)];
    let newActive = group.activeTabId;
    if (newActive === tabId) {
      if (newTabs.length > 0) {
        const newIdx = Math.min(ti, newTabs.length - 1);
        newActive = newTabs[newIdx]!.id;
      } else {
        newActive = undefined;
      }
    }
    const updatedGroup: TabGroupState = { ...group, tabs: newTabs, activeTabId: newActive };
    return ok([...groups.slice(0, gi), updatedGroup, ...groups.slice(gi + 1)], 'TAB_REMOVED', `Removed tab ${tabId}`);
  }
  return err('TAB_NOT_FOUND', `Tab ${tabId} not found in any group`);
}

export function activateTab(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  const result = groups.map((group) => {
    const hasTab = group.tabs.some((t) => t.id === tabId);
    if (hasTab) {
      const updatedTabs = group.tabs.map((t) =>
        t.id === tabId && t.isPreview ? { ...t, isPreview: false } : t
      );
      return { ...group, tabs: updatedTabs, activeTabId: tabId, isActive: true };
    }
    return { ...group, isActive: false };
  });
  const found = result.some((g) => g.tabs.some((t) => t.id === tabId));
  if (!found) return err('TAB_NOT_FOUND', `Tab ${tabId} not found`);
  return ok(result, 'TAB_ACTIVATED', `Activated tab ${tabId}`);
}

export function pinTab(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  return updateTabField(groups, tabId, { isPinned: true }, 'TAB_PINNED', `Pinned tab ${tabId}`);
}

export function unpinTab(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  return updateTabField(groups, tabId, { isPinned: false }, 'TAB_UNPINNED', `Unpinned tab ${tabId}`);
}

function updateTabField(
  groups: TabGroupState[],
  tabId: TabId,
  fields: Partial<{ isPinned: boolean; isPreview: boolean; isDirty: boolean }>,
  code: string,
  message: string
): EditorResult<TabGroupState[]> {
  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi]!;
    const ti = group.tabs.findIndex((t) => t.id === tabId);
    if (ti === -1) continue;
    const updatedTab = { ...group.tabs[ti]!, ...fields };
    const updatedTabs: TabState[] = [...group.tabs.slice(0, ti), updatedTab, ...group.tabs.slice(ti + 1)];
    const updatedGroup: TabGroupState = { ...group, tabs: updatedTabs };
    return ok([...groups.slice(0, gi), updatedGroup, ...groups.slice(gi + 1)], code, message);
  }
  return err('TAB_NOT_FOUND', `Tab ${tabId} not found`);
}

export function moveTab(
  groups: TabGroupState[],
  tabId: TabId,
  targetGroupId: TabGroupId,
  index?: number
): EditorResult<TabGroupState[]> {
  let tab: TabState | undefined;
  let sourceGroupIdx = -1;
  for (let gi = 0; gi < groups.length; gi++) {
    const found = groups[gi]!.tabs.find((t) => t.id === tabId);
    if (found) {
      tab = found;
      sourceGroupIdx = gi;
      break;
    }
  }
  if (!tab || sourceGroupIdx === -1) return err('TAB_NOT_FOUND', `Tab ${tabId} not found`);
  const removeResult = removeTab(groups, tabId);
  if (!removeResult.success) return removeResult;
  const movedTab: TabState = { ...tab, groupId: targetGroupId };
  return addTab(removeResult.value, targetGroupId, movedTab);
}

export function moveTabToNewGroup(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  let tab: TabState | undefined;
  for (const group of groups) {
    const found = group.tabs.find((t) => t.id === tabId);
    if (found) { tab = found; break; }
  }
  if (!tab) return err('TAB_NOT_FOUND', `Tab ${tabId} not found`);
  const removeResult = removeTab(groups, tabId);
  if (!removeResult.success) return removeResult;
  const newGroupId = asTabGroupId(generateId('tg'));
  const movedTab: TabState = { ...tab, groupId: newGroupId };
  const newGroup: TabGroupState = {
    id: newGroupId,
    tabs: [movedTab],
    activeTabId: tabId,
    isActive: true,
  };
  const deactivated = removeResult.value.map((g) => ({ ...g, isActive: false }));
  return ok([...deactivated, newGroup], 'TAB_MOVED_NEW_GROUP', `Moved tab ${tabId} to new group`);
}

export function closeOtherTabs(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  const result = groups.map((group) => {
    const hasTab = group.tabs.some((t) => t.id === tabId);
    if (!hasTab) return group;
    const kept = group.tabs.filter((t) => t.id === tabId || t.isPinned);
    return { ...group, tabs: kept, activeTabId: tabId };
  });
  return ok(result, 'OTHER_TABS_CLOSED', `Closed other tabs, kept ${tabId}`);
}

export function closeTabsToRight(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  const result = groups.map((group) => {
    const idx = group.tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return group;
    const kept = group.tabs.filter((t, i) => i <= idx || t.isPinned);
    const active = kept.some((t) => t.id === group.activeTabId) ? group.activeTabId : tabId;
    return { ...group, tabs: kept, activeTabId: active };
  });
  return ok(result, 'TABS_RIGHT_CLOSED', `Closed tabs to the right of ${tabId}`);
}

export function closeSavedTabs(groups: TabGroupState[]): EditorResult<TabGroupState[]> {
  const result = groups.map((group) => {
    const kept = group.tabs.filter((t) => t.isDirty || t.isPinned);
    const active = kept.some((t) => t.id === group.activeTabId)
      ? group.activeTabId
      : kept.length > 0 ? kept[0]!.id : undefined;
    return { ...group, tabs: kept, activeTabId: active };
  });
  return ok(result, 'SAVED_TABS_CLOSED', 'Closed all saved (non-dirty) tabs');
}

export function reorderTab(
  groups: TabGroupState[],
  tabId: TabId,
  newIndex: number
): EditorResult<TabGroupState[]> {
  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi]!;
    const currentIdx = group.tabs.findIndex((t) => t.id === tabId);
    if (currentIdx === -1) continue;
    const tab = group.tabs[currentIdx]!;
    const withoutTab = [...group.tabs.slice(0, currentIdx), ...group.tabs.slice(currentIdx + 1)];
    const clampedIdx = Math.max(0, Math.min(newIndex, withoutTab.length));
    const reordered: TabState[] = [...withoutTab.slice(0, clampedIdx), tab, ...withoutTab.slice(clampedIdx)];
    const updated: TabGroupState = { ...group, tabs: reordered };
    return ok([...groups.slice(0, gi), updated, ...groups.slice(gi + 1)], 'TAB_REORDERED', `Reordered tab ${tabId} to index ${clampedIdx}`);
  }
  return err('TAB_NOT_FOUND', `Tab ${tabId} not found`);
}

export function findTabByUri(groups: TabGroupState[], uri: string): TabState | undefined {
  for (const group of groups) {
    const tab = group.tabs.find((t) => t.uri === uri);
    if (tab) return tab;
  }
  return undefined;
}

export function dirtyTabs(groups: TabGroupState[]): TabState[] {
  const result: TabState[] = [];
  for (const group of groups) {
    for (const tab of group.tabs) {
      if (tab.isDirty) result.push(tab);
    }
  }
  return result;
}

export function previewTab(groups: TabGroupState[], tabId: TabId): EditorResult<TabGroupState[]> {
  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi]!;
    const ti = group.tabs.findIndex((t) => t.id === tabId);
    if (ti === -1) continue;
    const tab = group.tabs[ti]!;
    const updatedTab = { ...tab, isPreview: !tab.isPreview };
    const updatedTabs: TabState[] = [...group.tabs.slice(0, ti), updatedTab, ...group.tabs.slice(ti + 1)];
    const updated: TabGroupState = { ...group, tabs: updatedTabs };
    return ok([...groups.slice(0, gi), updated, ...groups.slice(gi + 1)], 'TAB_PREVIEW_TOGGLED', `Toggled preview for tab ${tabId}`);
  }
  return err('TAB_NOT_FOUND', `Tab ${tabId} not found`);
}
